import { FormikHelpers } from "formik";
import _ from "lodash";
import { ReactElement } from "react";

import {
  UpdateShortCodeOptInKeywordMutation,
  UpdateShortCodeOptInKeywordMutationVariables,
} from "~frontend/__generated__/graphql";
import { submitErrorField } from "~frontend/components/forms/FormikErrorBanner";
import { MediaFieldValue } from "~frontend/components/forms/FormikMediaField";
import { UPDATE_SHORT_CODE_OPT_IN_KEYWORD } from "~frontend/graphql/shortcode";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import KeywordDialogForm, { KeywordValues } from "./KeywordDialogForm";
import { ShortcodeOptInKeyword } from "./types";

export default function UpdateKeywordDialog({
  keyword,
  onClose,
  shortcodePhoneNumber,
}: {
  keyword: ShortcodeOptInKeyword;
  onClose: () => void;
  shortcodePhoneNumber: string;
}): ReactElement {
  const [updateKeyword] = useMutation<
    UpdateShortCodeOptInKeywordMutation,
    UpdateShortCodeOptInKeywordMutationVariables
  >(UPDATE_SHORT_CODE_OPT_IN_KEYWORD);

  const getErrorMessage = useErrorMessageFormatter();

  const { uploadMedia } = useMediaUpload();

  async function onSubmit(
    values: KeywordValues,
    formik: FormikHelpers<KeywordValues>
  ) {
    try {
      const mediaUpload = await uploadMedia(values.media as MediaFieldValue);
      await updateKeyword({
        variables: {
          input: {
            shortCodeOptInKeywordId: keyword.id,
            keyword: values.keyword,
            additionalWelcomeMessage: values.script || null,
            mediaUploadId: mediaUpload?.id || null,
          },
        },
      });
      onClose();
    } catch (e) {
      formik.setErrors({
        [submitErrorField]: getErrorMessage(e),
      });
    }
  }

  return (
    <KeywordDialogForm
      open
      onClose={onClose}
      onSubmit={onSubmit}
      initialValues={{
        keyword: keyword.normalizedKeyword,
        script: keyword.additionalWelcomeMessage || "",
        [submitErrorField]: null,
        media: keyword.mediaUpload
          ? { upload: keyword.mediaUpload }
          : undefined,
      }}
      dialogTitle={"Edit Opt In Keyword"}
      dialogCopy={
        "The message entered below will be sent in addition to the standard opt in welcome message that is specified in your short code contract. You can leave it blank to send just the standard welcome message."
      }
      shortcodePhoneNumber={shortcodePhoneNumber}
    />
  );
}
